import React, { createContext, useContext, useState, useCallback } from "react";
import { membersService } from "@/services/members.service";

interface ManagedMembersContextType {
  managedMembers: any[];
  setManagedMembers: React.Dispatch<React.SetStateAction<any[]>>;
  fetchManagedMembers: (force?: boolean) => Promise<any[]>;
  clearManagedMembers: () => void;
  loading: boolean;
  error: string | null;
}

const ManagedMembersContext = createContext<
  ManagedMembersContextType | undefined
>(undefined);

export function ManagedMembersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [managedMembers, setManagedMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const didFetch = React.useRef(false);
  const currentUserId = React.useRef<string | null>(null);
  const clearManagedMembers = useCallback(() => {
    setManagedMembers([]);
    didFetch.current = false;
    currentUserId.current = null;
    setError(null);
  }, []);

  const fetchManagedMembers = useCallback(
    async (force?: boolean): Promise<any[]> => {
      // Check if user has changed by comparing with stored member ID
      const storedMember = localStorage.getItem("member");
      const loggedInUserId = storedMember ? JSON.parse(storedMember).id : null;

      // If user changed, clear cache and force refetch
      if (currentUserId.current && currentUserId.current !== loggedInUserId) {
        didFetch.current = false;
        setManagedMembers([]);
      }
      currentUserId.current = loggedInUserId;

      // Return value lets callers that need the fresh list synchronously
      // (e.g. looking up a member right before navigating) avoid the
      // re-render race of reading the context's own `managedMembers` state
      // immediately after awaiting this call.
      if (didFetch.current && !force) return managedMembers;
      setLoading(true);
      setError(null);
      try {
        const data = await membersService.getManagedMembers();
        setManagedMembers(data || []);
        didFetch.current = true;
        return data || [];
      } catch (err: any) {
        setError(err?.message || "Failed to fetch managed members");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [managedMembers]
  );

  return (
    <ManagedMembersContext.Provider
      value={{
        managedMembers,
        setManagedMembers,
        fetchManagedMembers,
        clearManagedMembers,
        loading,
        error,
      }}
    >
      {children}
    </ManagedMembersContext.Provider>
  );
}

export function useManagedMembersContext() {
  const ctx = useContext(ManagedMembersContext);
  if (!ctx)
    throw new Error(
      "useManagedMembersContext must be used within ManagedMembersProvider"
    );
  return ctx;
}
